import { useState } from "react";
import { UnstyledButton } from "../../components/Button";
import { Loading } from "../../components/Loading";
import { Modal } from "../../components/Modal";
import Image from "next/image";
import { TabContent, TabList, TabRoot, TabTrigger } from "../../components/Tab";
import { styled } from "../../stitches.config";
import { screenshotImgUrl } from "../BigSpaceList";
import { Category, CATEGORY, GALLERY_TEMPLATES, Template } from "./data";
import { useTemplateImport } from "./useTemplateImport";

export function TemplateGallery({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<Category["id"]>("chill");
  const { importTemplate, importing } = useTemplateImport();

  return (
    <Modal title="Template" open={open} onClose={onClose}>
      <TRoot
        value={tab}
        orientation="vertical"
        onValueChange={(tab) => setTab(tab as Category["id"])}
      >
        <TList aria-label="select template type">
          {CATEGORY.map((c) => {
            return (
              <TabTrigger key={c.id} value={c.id}>
                {c.title}
              </TabTrigger>
            );
          })}
        </TList>
        {CATEGORY.map((c) => {
          if (!GALLERY_TEMPLATES[c.id]) return null;

          return (
            <TabContent key={c.id} value={c.id}>
              <List>
                {GALLERY_TEMPLATES[c.id].map((x) => (
                  <Item
                    importing={x.spaceId === importing}
                    key={x.spaceId}
                    template={x}
                    onSelect={importTemplate}
                  />
                ))}
              </List>
            </TabContent>
          );
        })}
      </TRoot>
    </Modal>
  );
}

export function Item({
  template,
  importing,
  onSelect,
}: {
  template: Template;
  importing?: boolean;
  onSelect: (tmpl: Template) => void;
}) {
  const imgSrc =
    template.screenshot ?? screenshotImgUrl({ id: template.spaceId });

  return (
    <ListItem importing={importing}>
      <figure>
        <LoadingContainer>{importing ? <Loading /> : null}</LoadingContainer>
        <UnstyledButton onClick={() => onSelect(template)}>
          <Image
            className="tmpl-gallery-img"
            src={imgSrc}
            alt={template.name}
            width={2798}
            height={1572}
          />
        </UnstyledButton>
        <figcaption>{template.name}</figcaption>
      </figure>
    </ListItem>
  );
}

const TRoot = styled(TabRoot, {
  width: "90vw",
  maxWidth: "960px",
  margin: "0 auto",
  height: `calc(100vh - ${2 * 80}px)`,
  color: "$paper40",
  fontWeight: 500,
  display: "flex",
  flexDirection: "column",

  [`& ${TabContent}`]: {
    flexGrow: 1,
    overflow: "scroll",
  },
});

const TList = styled(TabList, {
  marginBottom: "$4",
});

const List = styled("ul", {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  columnGap: "$4",
  rowGap: "$4",
});

const ListItem = styled("li", {
  position: "relative",
  variants: {
    importing: {
      true: {
        "& img": {
          opacity: 0.5,
        },
      },
    },
  },
  "& .tmpl-gallery-img": {
    display: "block",
    width: "100%",
    aspectRatio: "16 / 9",
    objectFit: "cover",
    border: "1px solid $paper60_20 !important",
    borderRadius: 4,
    "&:hover": {
      borderColor: "$paper60 !important",
    },
  },
  [`& ${UnstyledButton}`]: {
    marginBottom: "$1",
  },
});

const LoadingContainer = styled("div", {
  position: "absolute",
  left: "50%",
  top: "50%",
  transform: "translate(-50%, -50%)",
});
